import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { selectLunaMessages, selectLunaPagination } from "./lunaSelectors";
import {
  lunaSocket,
  LunaMessage,
  LunaMessageUpdate,
  LunaAttachment,
  LunaSessionUpdate,
  AnswerActionParams,
  AnswerActionReply,
  ToolCall,
  ToolExecution,
  UIBlock,
  ArtifactUpdate,
} from "services/luna/lunaSocket";
import {
  applyMessageUpdate,
  keepSettledCard,
  readHistoryToEnd,
} from "services/luna/lunaCards";
import { queryClient } from "App";
import {
  LunaSession,
  LunaPlaybook,
  Artifact,
  LunaRequest,
  useGetLunaSessions,
  useGetLunaPlaybooks,
  useLunaApi,
  useGetArtifact,
  useGetLunaRequests,
} from "services/luna/lunaApi";

// Constants
const LAST_ACTIVE_SESSION_KEY = "luna:lastActiveSessionId";
const MESSAGE_PAGE_SIZE = 20;

// Merges one message into a copy of a session's messages, in place. Returns
// whether it was taken: an older copy of a message already held is skipped.
const mergeMessage = (
  sessionMessages: Record<string, LunaMessage>,
  message: LunaMessage,
): boolean => {
  const existing = sessionMessages[message.id];

  // If this is a real user message (not temp), remove any temp user messages
  // This prevents duplicates from optimistic updates
  if (message.role === "user" && !message.id.startsWith("temp-")) {
    const tempKeys = Object.keys(sessionMessages).filter(
      (key) =>
        key.startsWith("temp-") && sessionMessages[key].role === "user",
    );
    tempKeys.forEach((key) => {
      console.log("[Luna Store] Removing temp message:", key);
      delete sessionMessages[key];
    });
  }

  // Only update if newer or doesn't exist
  if (
    !existing ||
    !existing.updated_at ||
    !message.updated_at ||
    message.updated_at >= existing.updated_at
  ) {
    sessionMessages[message.id] = keepSettledCard(existing, message);
    return true;
  }
  return false;
};
const ARTIFACT_VIEWER_WIDTH_KEY = "luna:artifactViewerWidth";
const DEFAULT_ARTIFACT_VIEWER_WIDTH = 500;
const MIN_ARTIFACT_VIEWER_WIDTH = 300;
const MAX_ARTIFACT_VIEWER_WIDTH = 1200;

const getSavedArtifactViewerWidth = (): number => {
  try {
    const saved = localStorage.getItem(ARTIFACT_VIEWER_WIDTH_KEY);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= MIN_ARTIFACT_VIEWER_WIDTH && parsed <= MAX_ARTIFACT_VIEWER_WIDTH) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_ARTIFACT_VIEWER_WIDTH;
};

// Types
interface PaginationState {
  hasMore: boolean;
  isLoadingMore: boolean;
  oldestMessageDate: string | null;
}

interface ToolExecutionUpdate {
  id: string;
  status: string;
  tool_name?: string;
  tool_label?: string;
  tool_call_id?: string;
  response_blocks?: UIBlock[];
  call_blocks?: UIBlock[];
  confirmation_message?: string;
  is_thinking?: boolean;
}

export interface LunaState {
  // Session state
  activeSessionId: string | null;
  sessions: Record<string, LunaSession>;

  // Messages state (Map-like for dedupe)
  messages: Record<string, Record<string, LunaMessage>>;

  // Pagination state per session
  pagination: Record<string, PaginationState>;

  // Thinking state per session (from message.is_thinking)
  isThinking: Record<string, boolean>;

  // Tool update counter per session (for triggering auto-scroll)
  toolUpdateCount: Record<string, number>;

  // Bumped to ask the session's composer to take focus
  composerFocusRequest: Record<string, number>;

  // UI state
  isSessionsPanelOpen: boolean;
  isConnecting: Record<string, boolean>;
  isLoadingHistory: Record<string, boolean>;
  error: string | null;

  // Artifact viewer state
  selectedArtifactId: string | null;
  artifactViewerWidth: number;

  // Actions
  setActiveSession: (sessionId: string | null) => void;
  connectToSession: (sessionId: string, options?: { isPlaybook?: boolean }) => Promise<void>;
  disconnectFromSession: (sessionId: string) => void;
  sendMessage: (
    sessionId: string,
    content: string,
    attachments?: LunaAttachment[],
  ) => Promise<void>;
  loadMoreMessages: (sessionId: string) => Promise<void>;
  refreshHistory: (sessionId: string) => Promise<void>;
  confirmTool: (sessionId: string, executionId: string) => Promise<void>;
  denyTool: (
    sessionId: string,
    executionId: string,
    reason?: string,
  ) => Promise<void>;
  answerAction: (
    sessionId: string,
    params: AnswerActionParams,
  ) => Promise<AnswerActionReply>;
  requestComposerFocus: (sessionId: string) => void;
  rateMessage: (
    sessionId: string,
    messageId: string,
    rating: "good" | "bad",
  ) => Promise<void>;
  toggleSessionsPanel: () => void;
  clearError: () => void;
  addSession: (session: LunaSession) => void;
  openArtifact: (artifactId: string) => void;
  closeArtifact: () => void;
  setArtifactViewerWidth: (width: number) => void;

  // Internal actions
  _upsertMessage: (sessionId: string, message: LunaMessage) => void;
  _upsertMessages: (sessionId: string, messages: LunaMessage[]) => void;
  _applyMessageUpdate: (sessionId: string, update: LunaMessageUpdate) => void;
  _updateSession: (sessionId: string, update: Partial<LunaSession>) => void;
  _updateToolExecution: (
    sessionId: string,
    execution: ToolExecutionUpdate,
  ) => void;
}

export const useLunaStore = create<LunaState>((set, get) => ({
  // Initial state
  activeSessionId: null,
  sessions: {},
  messages: {},
  pagination: {},
  isThinking: {},
  toolUpdateCount: {},
  composerFocusRequest: {},
  isSessionsPanelOpen: false,
  isConnecting: {},
  isLoadingHistory: {},
  error: null,
  selectedArtifactId: null,
  artifactViewerWidth: getSavedArtifactViewerWidth(),

  // Actions
  setActiveSession: (sessionId) => {
    set({ activeSessionId: sessionId });
    if (sessionId) {
      localStorage.setItem(LAST_ACTIVE_SESSION_KEY, sessionId);
    }
  },

  addSession: (session) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [session.id]: session,
      },
    }));
  },

  openArtifact: (artifactId) => {
    const { activeSessionId } = get();
    set({ selectedArtifactId: artifactId });
    // Update URL without triggering React Router re-render
    if (activeSessionId) {
      window.history.pushState(null, "", `/luna/requests/${activeSessionId}/artifact/${artifactId}`);
    }
  },

  closeArtifact: () => {
    const { activeSessionId } = get();
    set({ selectedArtifactId: null });
    // Update URL without triggering React Router re-render
    if (activeSessionId) {
      window.history.pushState(null, "", `/luna/requests/${activeSessionId}`);
    }
  },

  setArtifactViewerWidth: (width) => {
    set({ artifactViewerWidth: width });
  },

  connectToSession: async (sessionId, options) => {
    const {
      _upsertMessage,
      _applyMessageUpdate,
      _updateSession,
      _updateToolExecution,
    } = get();
    const isPlaybook = options?.isPlaybook ?? false;

    console.log("[Luna Store] connectToSession called:", sessionId, { isPlaybook });
    set((state) => ({
      isConnecting: { ...state.isConnecting, [sessionId]: true },
      isLoadingHistory: { ...state.isLoadingHistory, [sessionId]: true },
      error: null,
    }));

    try {
      // Register handlers before connecting
      console.log("[Luna Store] Registering message handler for:", sessionId);
      lunaSocket.onMessage(sessionId, (message) => {
        console.log("[Luna Store] Handler called with message:", message.id);
        _upsertMessage(sessionId, message);
      });

      lunaSocket.onMessageUpdate(sessionId, (update) => {
        _applyMessageUpdate(sessionId, update);
      });

      lunaSocket.onSessionUpdate(sessionId, (session) => {
        _updateSession(sessionId, session);
      });

      lunaSocket.onToolUpdate(sessionId, (execution) => {
        console.log("[Luna Store] Tool update received:", execution);
        _updateToolExecution(sessionId, execution);
      });

      lunaSocket.onArtifactUpdate(sessionId, (artifactUpdate: ArtifactUpdate) => {
        console.log("[Luna Store] Artifact update received:", artifactUpdate.id);
        // Update the React Query cache for this artifact
        const queryKey = ["luna-artifact", artifactUpdate.id];
        queryClient.setQueryData(queryKey, (oldData: { success: boolean; data: Artifact } | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              title: artifactUpdate.title,
              description: artifactUpdate.description,
              label: artifactUpdate.label,
              type: artifactUpdate.type,
              content: artifactUpdate.content,
            } as Artifact,
          };
        });
      });

      // Connect to WebSocket channel
      lunaSocket.connect(sessionId);

      // Helper to retry async operation with backoff
      const withRetry = async <T>(
        fn: () => Promise<T>,
        maxRetries = 3,
        delayMs = 200,
      ): Promise<T> => {
        for (let i = 0; i < maxRetries; i++) {
          try {
            return await fn();
          } catch (err) {
            if (i === maxRetries - 1) throw err;
            await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
          }
        }
        throw new Error("Max retries exceeded");
      };

      // Load initial history with retry (channel may not be fully joined yet)
      // Only fetch session data for playbook sessions (to get the title)
      const [historyResponse, sessionData] = await Promise.all([
        withRetry(() =>
          lunaSocket.getHistory(sessionId, MESSAGE_PAGE_SIZE, 0),
        ),
        isPlaybook ? withRetry(() => lunaSocket.getSession(sessionId)) : Promise.resolve(null),
      ]);

      const { messages: history, is_thinking, has_more: hasMore } = historyResponse;

      // Set initial thinking state from history response
      // Only set to true if there are messages OR it's a playbook session
      // (playbooks can start thinking immediately, regular new sessions can't)
      set((state) => ({
        isThinking: {
          ...state.isThinking,
          [sessionId]: (history.length > 0 || isPlaybook) && is_thinking,
        },
      }));

      // Upsert all history messages (dedupe handles any live messages that arrived)
      history.forEach((msg) => _upsertMessage(sessionId, msg));

      // Track oldest message for pagination
      const oldestMessage = history[0];
      set((state) => ({
        pagination: {
          ...state.pagination,
          [sessionId]: {
            hasMore,
            isLoadingMore: false,
            oldestMessageDate: oldestMessage?.created_at || null,
          },
        },
      }));

      // Update session with data from get:session (includes title)
      if (sessionData) {
        _updateSession(sessionId, sessionData);
      }

      set((state) => ({
        isConnecting: { ...state.isConnecting, [sessionId]: false },
        isLoadingHistory: { ...state.isLoadingHistory, [sessionId]: false },
      }));
    } catch (err) {
      console.error("Failed to connect to Luna session:", err);
      set((state) => ({
        isConnecting: { ...state.isConnecting, [sessionId]: false },
        isLoadingHistory: { ...state.isLoadingHistory, [sessionId]: false },
        error: "Failed to connect to session",
      }));
      throw err;
    }
  },

  disconnectFromSession: (sessionId) => {
    lunaSocket.disconnect(sessionId);
  },

  sendMessage: async (sessionId, content, attachments) => {
    const { _upsertMessage } = get();

    // Optimistically add user message (with attachments so the chips render
    // immediately on the user's own bubble before the server echoes back).
    const optimisticMessage: LunaMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content,
      is_thinking: false,
      created_at: new Date().toISOString(),
      status: "pending",
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
    };
    _upsertMessage(sessionId, optimisticMessage);

    // Update last active
    localStorage.setItem(LAST_ACTIVE_SESSION_KEY, sessionId);

    try {
      await lunaSocket.sendMessage(sessionId, content, attachments);
    } catch (err) {
      // Mark message as failed and surface the server's clean error message
      // (e.g. the 50 MB conversation cap) when there is one.
      _upsertMessage(sessionId, { ...optimisticMessage, status: "failed" });
      set({
        error: err instanceof Error ? err.message : "Failed to send message",
      });
      throw err;
    }
  },

  loadMoreMessages: async (sessionId) => {
    const { _upsertMessage, pagination, messages } = get();
    const paginationState = pagination[sessionId];

    // Don't load if already loading or no more messages
    if (!paginationState?.hasMore || paginationState.isLoadingMore) {
      return;
    }

    // Get current message count for offset
    const currentMessages = Object.values(messages[sessionId] || {});
    const offset = currentMessages.length;

    // Set loading state
    set((state) => ({
      pagination: {
        ...state.pagination,
        [sessionId]: {
          ...state.pagination[sessionId],
          isLoadingMore: true,
        },
      },
    }));

    try {
      const { messages: olderMessages, has_more: hasMore } = await lunaSocket.getHistory(
        sessionId,
        MESSAGE_PAGE_SIZE,
        offset,
      );

      // Upsert older messages
      olderMessages.forEach((msg) => _upsertMessage(sessionId, msg));

      // Update pagination state
      const oldestMessage = olderMessages[0];
      set((state) => ({
        pagination: {
          ...state.pagination,
          [sessionId]: {
            hasMore,
            isLoadingMore: false,
            oldestMessageDate: oldestMessage?.created_at || state.pagination[sessionId]?.oldestMessageDate || null,
          },
        },
      }));
    } catch (err) {
      console.error("Failed to load more messages:", err);
      set((state) => ({
        pagination: {
          ...state.pagination,
          [sessionId]: {
            ...state.pagination[sessionId],
            isLoadingMore: false,
          },
        },
      }));
    }
  },

  refreshHistory: async (sessionId) => {
    const { _upsertMessages } = get();

    try {
      // Read through to the newest message: that catches new messages and any
      // card rewritten while we weren't subscribed, wherever it sits.
      const { is_thinking, has_more: hasMore } = await readHistoryToEnd(
        (limit, offset) => lunaSocket.getHistory(sessionId, limit, offset),
        (page) => _upsertMessages(sessionId, page),
      );
      // Update thinking state from refresh
      set((state) => ({
        isThinking: {
          ...state.isThinking,
          [sessionId]: is_thinking,
        },
        pagination: state.pagination[sessionId]
          ? {
              ...state.pagination,
              [sessionId]: { ...state.pagination[sessionId], hasMore },
            }
          : state.pagination,
      }));
    } catch (err) {
      // Silently ignore - this is just a fallback refresh
      console.debug("Failed to refresh history:", err);
    }
  },

  confirmTool: async (sessionId, executionId) => {
    try {
      await lunaSocket.confirmTool(sessionId, executionId);
    } catch (err) {
      set({ error: "Failed to confirm tool" });
      throw err;
    }
  },

  denyTool: async (sessionId, executionId, reason) => {
    try {
      await lunaSocket.denyTool(sessionId, executionId, reason);
    } catch (err) {
      set({ error: "Failed to deny tool" });
      throw err;
    }
  },

  // Refusals come back in the reply for the card to show; a channel error or
  // timeout rejects. Neither touches the session's error banner.
  answerAction: (sessionId, params) => lunaSocket.answerAction(sessionId, params),

  requestComposerFocus: (sessionId) => {
    set((state) => ({
      composerFocusRequest: {
        ...state.composerFocusRequest,
        [sessionId]: (state.composerFocusRequest[sessionId] || 0) + 1,
      },
    }));
  },

  rateMessage: async (sessionId, messageId, rating) => {
    const { _upsertMessage, messages } = get();
    const message = messages[sessionId]?.[messageId];

    if (message) {
      // Optimistic update
      _upsertMessage(sessionId, { ...message, rating });
    }

    // Note: API call would go here - for now we just do optimistic update
    // The actual API integration can be added when the backend endpoint is ready
  },

  toggleSessionsPanel: () => {
    set((state) => ({ isSessionsPanelOpen: !state.isSessionsPanelOpen }));
  },

  clearError: () => {
    set({ error: null });
  },

  // Internal actions
  _upsertMessage: (sessionId, message) => {
    console.log("[Luna Store] _upsertMessage called:", {
      sessionId,
      messageId: message.id,
      role: message.role,
      is_thinking: message.is_thinking,
    });
    set((state) => {
      const sessionMessages = { ...(state.messages[sessionId] || {}) };
      if (mergeMessage(sessionMessages, message)) {
        console.log("[Luna Store] Adding/updating message:", message.id);
        return {
          // Update isThinking from the message's is_thinking field
          isThinking: {
            ...state.isThinking,
            [sessionId]: message.is_thinking,
          },
          messages: {
            ...state.messages,
            [sessionId]: sessionMessages,
          },
        };
      }
      console.log("[Luna Store] Skipping message (older):", message.id);
      return state;
    });
  },

  // A page of history in one write. Leaves isThinking to the caller, which has
  // the page's own thinking state.
  _upsertMessages: (sessionId, messages) => {
    set((state) => {
      const sessionMessages = { ...(state.messages[sessionId] || {}) };
      messages.forEach((message) => mergeMessage(sessionMessages, message));
      return {
        messages: { ...state.messages, [sessionId]: sessionMessages },
      };
    });
  },

  // A card rewritten in place. Unlike a new message it says nothing about
  // whether Luna is still thinking.
  _applyMessageUpdate: (sessionId, update) => {
    set((state) => {
      const sessionMessages = applyMessageUpdate(state.messages[sessionId], update);
      if (!sessionMessages) return state;
      return {
        messages: { ...state.messages, [sessionId]: sessionMessages },
      };
    });
  },

  _updateSession: (sessionId, update) => {
    console.log("[Luna Store] _updateSession called:", {
      sessionId,
      status: update.status,
      update,
    });
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionId]: {
          ...state.sessions[sessionId],
          ...update,
          id: sessionId,
        } as LunaSession,
      },
    }));
  },

  _updateToolExecution: (sessionId, execution) => {
    console.log("[Luna Store] _updateToolExecution called:", {
      sessionId,
      executionId: execution.id,
      status: execution.status,
      tool_call_id: execution.tool_call_id,
      is_thinking: execution.is_thinking,
    });
    set((state) => {
      const sessionMessages = state.messages[sessionId];
      console.log("[Luna Store] Session messages count:", sessionMessages ? Object.keys(sessionMessages).length : 0);

      if (!sessionMessages) {
        console.log("[Luna Store] No session messages found for:", sessionId);
        return state;
      }

      let updated = false;
      const updatedMessages: Record<string, LunaMessage> = {};

      for (const [msgId, message] of Object.entries(sessionMessages)) {
        // Check if this message has tool_calls that match this execution
        const toolCallIds = message.tool_calls?.map((tc) => tc.id) || [];
        const hasMatchingToolCall =
          message.role === "assistant" &&
          message.tool_calls?.some(
            (tc) => tc.id === execution.tool_call_id,
          );

        console.log("[Luna Store] Checking message:", {
          msgId,
          role: message.role,
          toolCallIds,
          lookingFor: execution.tool_call_id,
          hasMatchingToolCall,
        });

        if (hasMatchingToolCall) {
          // This message has a tool_call that matches - update or add to tool_executions
          const existingExecutions = message.tool_executions || [];
          const existingIndex = existingExecutions.findIndex(
            (exec) => exec.id === execution.id,
          );

          let updatedExecutions: ToolExecution[];

          if (existingIndex >= 0) {
            // Update existing execution
            updatedExecutions = existingExecutions.map(
              (exec): ToolExecution => {
                if (exec.id !== execution.id) {
                  return exec;
                }
                updated = true;
                return {
                  ...exec,
                  status: execution.status as ToolExecution["status"],
                  tool_name: execution.tool_name || exec.tool_name,
                  tool_label: execution.tool_label || exec.tool_label,
                  tool_call_id: execution.tool_call_id || exec.tool_call_id,
                  response_blocks:
                    execution.response_blocks || exec.response_blocks,
                  call_blocks: execution.call_blocks || exec.call_blocks,
                  confirmation_message: execution.confirmation_message,
                  completion_message: execution.completion_message,
                };
              },
            );
          } else {
            // Add new execution to this message
            updated = true;
            updatedExecutions = [
              ...existingExecutions,
              {
                id: execution.id,
                status: execution.status as ToolExecution["status"],
                tool_name: execution.tool_name,
                tool_label: execution.tool_label,
                tool_call_id: execution.tool_call_id,
                response_blocks: execution.response_blocks || [],
                call_blocks: execution.call_blocks || [],
                confirmation_message: execution.confirmation_message,
                completion_message: execution.completion_message,
              },
            ];
          }

          updatedMessages[msgId] = {
            ...message,
            tool_executions: updatedExecutions,
          };
        } else if (message.tool_executions) {
          // Check if execution exists in this message's tool_executions
          const existingIndex = message.tool_executions.findIndex(
            (exec) => exec.id === execution.id,
          );

          if (existingIndex >= 0) {
            // Update existing execution
            const updatedExecutions = message.tool_executions.map(
              (exec): ToolExecution => {
                if (exec.id !== execution.id) {
                  return exec;
                }
                updated = true;
                return {
                  ...exec,
                  status: execution.status as ToolExecution["status"],
                  tool_name: execution.tool_name || exec.tool_name,
                  tool_label: execution.tool_label || exec.tool_label,
                  tool_call_id: execution.tool_call_id || exec.tool_call_id,
                  response_blocks:
                    execution.response_blocks || exec.response_blocks,
                  call_blocks: execution.call_blocks || exec.call_blocks,
                  confirmation_message: execution.confirmation_message,
                  completion_message: execution.completion_message,
                };
              },
            );
            updatedMessages[msgId] = {
              ...message,
              tool_executions: updatedExecutions,
            };
          } else {
            updatedMessages[msgId] = message;
          }
        } else {
          updatedMessages[msgId] = message;
        }
      }

      if (!updated) {
        console.log("[Luna Store] No matching message found for tool execution");
        return state;
      }

      console.log("[Luna Store] Tool execution matched, updating state with is_thinking:", execution.is_thinking);

      // Build new state with updated thinking status if provided
      const newState: Partial<typeof state> = {
        // Increment tool update counter to trigger auto-scroll
        toolUpdateCount: {
          ...state.toolUpdateCount,
          [sessionId]: (state.toolUpdateCount[sessionId] || 0) + 1,
        },
        messages: {
          ...state.messages,
          [sessionId]: updatedMessages,
        },
      };

      // Update thinking state if is_thinking is provided
      if (execution.is_thinking !== undefined) {
        newState.isThinking = {
          ...state.isThinking,
          [sessionId]: execution.is_thinking,
        };
      }

      return newState as typeof state;
    });
  },
}));

// Selectors
export const useActiveSessionId = () =>
  useLunaStore((state) => state.activeSessionId);

export const useLunaSession = (sessionId: string | null) =>
  useLunaStore((state) => (sessionId ? state.sessions[sessionId] : null));

// useShallow is load-bearing here: selectLunaMessages derives a new array on
// every call, which zustand v5 would otherwise treat as a changed snapshot.
export const useLunaMessages = (sessionId: string | null) =>
  useLunaStore(useShallow(selectLunaMessages(sessionId)));

export const useLunaIsThinking = (sessionId: string | null) =>
  useLunaStore((state) =>
    sessionId ? state.isThinking[sessionId] ?? false : false,
  );

export const useLunaHasPendingTools = (sessionId: string | null) =>
  useLunaStore((state) => {
    if (!sessionId) return false;
    const session = state.sessions[sessionId];
    // Don't block input when waiting for external result - user can still send messages
    if (session?.status === "pending_external") return false;
    return (
      session?.status === "waiting_for_tool" ||
      (session?.pending_tool_count ?? 0) > 0
    );
  });

export const useIsSessionsPanelOpen = () =>
  useLunaStore((state) => state.isSessionsPanelOpen);

export const useLunaError = () => useLunaStore((state) => state.error);

export const useLunaPagination = (sessionId: string | null) =>
  useLunaStore(selectLunaPagination(sessionId));

export const useLunaToolUpdateCount = (sessionId: string | null) =>
  useLunaStore((state) =>
    sessionId ? state.toolUpdateCount[sessionId] ?? 0 : 0,
  );

export const useLunaComposerFocusRequest = (sessionId: string | null) =>
  useLunaStore((state) =>
    sessionId ? state.composerFocusRequest[sessionId] ?? 0 : 0,
  );

// A closed chat takes no replies, and its socket gets no pushes.
export const useLunaChatIsClosed = (sessionId: string) => {
  const session = useLunaSession(sessionId);
  const { data: requests } = useGetLunaRequests();
  const requestStatus = requests?.find((r) => r.sessionId === sessionId)?.status;
  return (
    requestStatus === "closed" ||
    session?.status === "closed" ||
    session?.status === "complete"
  );
};

export const useLunaIsConnecting = (sessionId: string | null) =>
  useLunaStore((state) =>
    sessionId ? state.isConnecting[sessionId] ?? false : false,
  );

export const useLunaIsLoadingHistory = (sessionId: string | null) =>
  useLunaStore((state) =>
    sessionId ? state.isLoadingHistory[sessionId] ?? false : false,
  );

export const useSelectedArtifactId = () =>
  useLunaStore((state) => state.selectedArtifactId);

export const useArtifactViewerWidth = () =>
  useLunaStore((state) => state.artifactViewerWidth);

// Utility
export const getLastActiveSessionId = (): string | null => {
  return localStorage.getItem(LAST_ACTIVE_SESSION_KEY);
};

// Re-export React Query hooks from service layer
export { useGetLunaSessions, useGetLunaPlaybooks, useLunaApi, useGetArtifact, useGetLunaRequests };

// Re-export types
export type { LunaSession, LunaMessage, LunaAttachment, LunaSessionUpdate, LunaPlaybook, Artifact, LunaRequest };
